import { Box, useMediaQuery, useTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import Loader from '../../components/Loader/Loader';
import { FloatingActionButton, Footer, Header, Hero, NavBar } from '../../components/_mui';
import { useFooterContext } from '../../contexts/FooterContext';
import { useIframe } from '../../hooks/useIframe';
import useKeepUrlParams from '../../hooks/useKeepUrlParams';
import { useLogoScale } from '../../hooks/useLogoScale';
import useWindowSize from '../../hooks/useWindowSize';
import { initApp } from '../../store/actions/init';
import { selectContentBackgroundColor } from '../../store/selectors/derived';
import {
  selectAccentColor,
  selectBaseColor,
  selectIsEmbed,
  selectIsLoading,
  selectIsMarketplaceBlocked,
} from '../../store/selectors/init';
import { selectIsInitialized } from '../../store/selectors/keys';
import { redactPostHogEvent } from '../../util/analytics';
import {
  getConvertedPathname,
  isAuthenticationRoute,
  isMarketplaceRoute,
  isResultRoute,
  setAccentColor,
  setBaseColor,
} from '../../util/helpers';

import variables from '../../_variables.scss';

interface IProps {
  children: React.ReactNode;
}

function App({ children }: IProps) {
  const { search: initString, pathname } = useLocation();
  const { mobile } = useWindowSize();
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));

  const convertedPathname = getConvertedPathname(pathname);
  const heroRef = useRef<HTMLDivElement>(null);
  const contentSectionRef = useRef<HTMLDivElement>(null);
  const { setHeroRef } = useFooterContext();

  const dispatch = useDispatch();
  const isInitialized = useSelector(selectIsInitialized);
  const isLoading = useSelector(selectIsLoading);
  const baseColor = useSelector(selectBaseColor);
  const accentColor = useSelector(selectAccentColor);
  const isEmbed = useSelector(selectIsEmbed);
  const isMarketplaceBlocked = useSelector(selectIsMarketplaceBlocked);
  const contentBgColor = useSelector(selectContentBackgroundColor);

  const { headerHeight } = useLogoScale();

  const posthogKey = process.env.REACT_APP_PUBLIC_POSTHOG_KEY;

  useEffect(() => {
    if (posthogKey) {
      // The flags request sends the initial page URL as person properties outside before_send.
      // This also turns off remote config, so session replay, surveys and heatmaps stay off too.
      posthog.init(posthogKey, { before_send: redactPostHogEvent, advanced_disable_flags: true });
    }
  }, [posthogKey]);

  useEffect(() => {
    if (!isInitialized) {
      dispatch(initApp({ pathname, initString }));
    }
  }, [dispatch, initString, isInitialized, pathname]);

  useIframe();
  useKeepUrlParams();

  useEffect(() => {
    if (baseColor) {
      setBaseColor(baseColor);
    }
    if (accentColor) {
      setAccentColor(accentColor);
    }
  }, [accentColor, baseColor]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    setHeroRef(heroRef);

    return () => setHeroRef(undefined);
  }, [setHeroRef]);

  const isMarketplace = isMarketplaceRoute(convertedPathname);
  const isHeroShown = !isMarketplace && !isMarketplaceBlocked;
  const isNavBarShown = isMobileViewport && !isMarketplace && !isMarketplaceBlocked;
  const isFabShown = (!isMobileViewport || isMarketplace) && !isMarketplaceBlocked;
  const isFooterShown =
    !isResultRoute(convertedPathname) && !isAuthenticationRoute(convertedPathname) && !isMarketplaceBlocked;

  const contentSectionMinHeight = useMemo(() => {
    const navBarHeight = mobile && !isMarketplace && !isMarketplaceBlocked ? parseInt(variables.navBarHeight, 10) : 0;

    return !isResultRoute(convertedPathname) && !isAuthenticationRoute(convertedPathname)
      ? `calc(100vh - ${headerHeight}px - ${navBarHeight}px)`
      : '100vh';
  }, [convertedPathname, headerHeight, mobile, isMarketplace, isMarketplaceBlocked]);

  const contentSectionPaddingBottom = useMemo(() => {
    return {
      ...(isFooterShown && {
        paddingBottom: `${parseInt(mobile ? variables.footerMobileHeight : variables.footerDesktopHeight, 10)}px`,
      }),
    };
  }, [isFooterShown, mobile]);

  const contentSectionBgColor = useMemo(() => {
    if (contentBgColor) {
      return contentBgColor;
    }

    return grey[100];
  }, [contentBgColor]);

  const contentSectionStyles = useMemo(() => {
    return {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      minHeight: contentSectionMinHeight,
      backgroundColor: contentSectionBgColor,
      ...contentSectionPaddingBottom,
    };
  }, [contentSectionBgColor, contentSectionPaddingBottom, contentSectionMinHeight]);

  const loader = useMemo(() => {
    return isLoading && <Loader isLoading />;
  }, [isLoading]);

  const headerSection = useMemo(() => {
    return (
      !isResultRoute(convertedPathname) &&
      !isAuthenticationRoute(convertedPathname) && (
        <>
          <Header />
          {isHeroShown && <Hero ref={heroRef} />}
          {isNavBarShown && <NavBar />}
        </>
      )
    );
  }, [convertedPathname, isHeroShown, isNavBarShown]);

  const contentSection = useMemo(() => {
    return (
      <Box ref={contentSectionRef} sx={contentSectionStyles} data-testid="content-section">
        {!isLoading && children}
      </Box>
    );
  }, [isLoading, children, contentSectionStyles]);

  const footerSection = useMemo(() => {
    return (
      <>
        {isFooterShown && <Footer />}
        {isFabShown && <FloatingActionButton />}
      </>
    );
  }, [isFooterShown, isFabShown]);

  const appContent = useMemo(
    () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', ...(isEmbed && { pointerEvents: 'none' }) }}>
        {loader}
        {headerSection}
        {contentSection}
        {footerSection}

        <ToastContainer
          autoClose={10000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          draggable
          pauseOnHover
          theme="colored"
        />
      </Box>
    ),
    [loader, headerSection, contentSection, footerSection],
  );

  return posthogKey ? <PostHogProvider client={posthog}>{appContent}</PostHogProvider> : appContent;
}

App.whyDidYouRender = false;

export default App;
